import { toast as toastify } from 'vue3-toastify';

/**
 * Centralized toast wrapper around vue3-toastify.
 *
 * Provides a consistent API and default options so that individual
 * components never import vue3-toastify directly.
 *
 * Usage:
 *   import { useToast } from '@/composables/useToast';
 *   const toast = useToast();
 *   toast.success('Salvato!');
 *   toast.error('Qualcosa è andato storto');
 */

const DEFAULTS = {
    hideProgressBar: true,
    position: toastify.POSITION.TOP_RIGHT,
    autoClose: 4000,
};

function mergeOptions(type, options = {}) {
    return { ...DEFAULTS, type, ...options };
}

/**
 * Show a raw toast (function-style).  Rarely needed directly —
 * prefer the typed helpers below.
 */
function show(message, options = {}) {
    if (import.meta.env.SSR) {
        return;
    }

    return toastify(message, { ...DEFAULTS, ...options });
}

function success(message, options = {}) {
    return show(message, mergeOptions('success', options));
}

function error(message, options = {}) {
    return show(message, mergeOptions('error', options));
}

function warning(message, options = {}) {
    return show(message, mergeOptions('warning', options));
}

function info(message, options = {}) {
    return show(message, mergeOptions('info', options));
}

const toastApi = { show, success, error, warning, info };

/**
 * Composable entry-point.
 * Returns the same singleton — safe to call from setup or plain JS.
 */
export function useToast() {
    return toastApi;
}

export default useToast;
